import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Plus, X, Save, Send, Upload, Copy, Trash2,
  Clock, CheckCircle, ChevronDown, ChevronRight, AlertCircle
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

interface Client {
  id: number;
  name: string;
}

interface PostType {
  id: number;
  name: string;
  category: string;
  description?: string;
  default_template?: string;
}

interface PlanPost {
  id?: number;
  post_type_id?: number;
  post_type_name: string;
  title: string;
  description?: string;
  scheduled_date: string;
  status: 'draft' | 'scheduled' | 'published' | 'cancelled';
  platform?: string; // Legacy field
  channels?: string[]; // Multi-channel distribution
  assets?: string[]; // page_content, infographic, images, video, podcast
  notes?: string;
  order_index: number;
}

interface SimplePlan {
  id?: number;
  client_id: number;
  name: string;
  description?: string;
  type: 'weekly' | 'monthly' | 'quarterly';
  start_date: string;
  end_date: string;
  status: 'draft' | 'active' | 'completed' | 'archived';
}

export default function SimplePlanBuilder() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [postTypes, setPostTypes] = useState<PostType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Plan details
  const [plan, setPlan] = useState<SimplePlan>({
    client_id: 0,
    name: '',
    description: '',
    type: 'monthly',
    start_date: '',
    end_date: '',
    status: 'draft'
  });

  // Posts
  const [posts, setPosts] = useState<PlanPost[]>([
    {
      post_type_name: '',
      title: '',
      scheduled_date: '',
      status: 'draft',
      channels: ['website'],
      assets: ['page_content'],
      order_index: 0
    }
  ]);

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['all']));

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (plan.client_id) {
      loadPostTypes(plan.client_id);
    }
  }, [plan.client_id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [clientsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/clients`)
      ]);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPostTypes = async (clientId: number) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/post-types`, {
        params: { client_id: clientId, is_active: true }
      });
      setPostTypes(response.data);
    } catch (error) {
      console.error('Failed to load post types:', error);
    }
  };

  const handlePlanChange = (field: keyof SimplePlan, value: any) => {
    setPlan(prev => ({ ...prev, [field]: value }));
  };

  const handlePostChange = (index: number, field: keyof PlanPost, value: any) => {
    const newPosts = [...posts];
    newPosts[index] = { ...newPosts[index], [field]: value };

    // If post type is selected, auto-fill name
    if (field === 'post_type_id' && value) {
      const postType = postTypes.find(pt => pt.id === parseInt(value));
      if (postType) {
        newPosts[index].post_type_name = postType.name;
        if (postType.description && !newPosts[index].description) {
          newPosts[index].description = postType.description;
        }
      }
    }

    setPosts(newPosts);
  };

  const addPost = () => {
    setPosts([...posts, {
      post_type_name: '',
      title: '',
      scheduled_date: '',
      status: 'draft',
      channels: ['website'],
      assets: ['page_content'],
      order_index: posts.length
    }]);
  };

  const removePost = (index: number) => {
    setPosts(posts.filter((_, i) => i !== index));
  };

  const duplicatePost = (index: number) => {
    const postToDuplicate = posts[index];
    const newPost = {
      ...postToDuplicate,
      id: undefined,
      title: `${postToDuplicate.title} (Copy)`,
      order_index: posts.length
    };
    setPosts([...posts, newPost]);
  };

  const savePlan = async (status: 'draft' | 'active' = 'draft') => {
    try {
      setSaving(true);

      // Validation
      if (!plan.client_id) {
        alert('Please select a client');
        return;
      }
      if (!plan.name) {
        alert('Please enter a plan name');
        return;
      }
      if (!plan.start_date || !plan.end_date) {
        alert('Please select start and end dates');
        return;
      }

      const validPosts = posts.filter(p => p.title && p.post_type_name && p.scheduled_date);
      if (validPosts.length === 0) {
        alert('Please add at least one post with title, type, and date');
        return;
      }

      // Create plan
      const planPayload = { ...plan, status };
      const planResponse = await axios.post(`${API_BASE_URL}/simple-plans`, planPayload);
      const createdPlan = planResponse.data;

      // Create posts
      const postsPayload = validPosts.map((post, index) => ({
        ...post,
        order_index: index
      }));

      await axios.post(`${API_BASE_URL}/simple-plans/${createdPlan.id}/posts`, postsPayload);

      alert(`Plan ${status === 'draft' ? 'saved as draft' : 'created and activated'} successfully!`);
      navigate('/dashboard/plans');
    } catch (error: any) {
      console.error('Failed to save plan:', error);
      alert(`Failed to save plan: ${error.response?.data?.error || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const importFromCSV = () => {
    // TODO: Implement CSV import
    alert('CSV import coming soon!');
  };

  const postTypesByCategory = postTypes.reduce((acc, pt) => {
    if (!acc[pt.category]) acc[pt.category] = [];
    acc[pt.category].push(pt);
    return acc;
  }, {} as Record<string, PostType[]>);

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      team_members: 'Team Members',
      services: 'Services',
      service_areas: 'Service Areas',
      news: 'News & Updates',
      events: 'Events',
      testimonials: 'Testimonials',
      tips: 'Tips & Advice',
      custom: 'Custom'
    };
    return labels[category] || category;
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Create Content Strategy</h1>
        <p className="text-gray-600 mt-1">Organize content by Post Type with multi-channel distribution and asset selection</p>
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Remember:</strong> All content goes on your website first for SEO, then distributes to other channels based on Post Type strategy.
          </p>
        </div>
      </div>

      {/* Plan Details Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Plan Details</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client *
            </label>
            <select
              value={plan.client_id}
              onChange={(e) => handlePlanChange('client_id', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={0}>Select a client...</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Plan Name *
            </label>
            <input
              type="text"
              value={plan.name}
              onChange={(e) => handlePlanChange('name', e.target.value)}
              placeholder="e.g., December 2024 Social Media"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Plan Type *
            </label>
            <select
              value={plan.type}
              onChange={(e) => handlePlanChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                value={plan.start_date}
                onChange={(e) => handlePlanChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <input
                type="date"
                value={plan.end_date}
                onChange={(e) => handlePlanChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={plan.description}
              onChange={(e) => handlePlanChange('description', e.target.value)}
              placeholder="Brief description of this content plan..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Posts Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Content Posts</h2>
              <p className="text-sm text-gray-600 mt-1">{posts.length} post{posts.length !== 1 ? 's' : ''} scheduled</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={importFromCSV}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Import CSV
              </button>
              <button
                onClick={addPost}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Post
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                  Date *
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                  Post Type *
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-64">
                  Title *
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-52">
                  Channels
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-52">
                  Assets
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {posts.map((post, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="date"
                      value={post.scheduled_date}
                      onChange={(e) => handlePostChange(index, 'scheduled_date', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={post.post_type_id || ''}
                      onChange={(e) => handlePostChange(index, 'post_type_id', e.target.value ? parseInt(e.target.value) : undefined)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select type...</option>
                      {Object.entries(postTypesByCategory).map(([category, types]) => (
                        <optgroup key={category} label={getCategoryLabel(category)}>
                          {types.map(type => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={post.title}
                      onChange={(e) => handlePostChange(index, 'title', e.target.value)}
                      placeholder="Post title..."
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {['website', 'google_business', 'social', 'email', 'youtube'].map(channel => (
                        <label key={channel} className="flex items-center text-xs whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={post.channels?.includes(channel)}
                            onChange={(e) => {
                              const currentChannels = post.channels || ['website'];
                              const newChannels = e.target.checked
                                ? [...currentChannels, channel]
                                : currentChannels.filter(c => c !== channel);
                              // Always keep website
                              if (!newChannels.includes('website')) {
                                newChannels.unshift('website');
                              }
                              handlePostChange(index, 'channels', newChannels);
                            }}
                            className="mr-1 h-3 w-3"
                          />
                          {channel === 'google_business' ? 'Google' : channel === 'youtube' ? 'YouTube' : channel.charAt(0).toUpperCase() + channel.slice(1)}
                        </label>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {['page_content', 'infographic', 'images', 'video', 'podcast'].map(asset => (
                        <label key={asset} className="flex items-center text-xs whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={post.assets?.includes(asset)}
                            onChange={(e) => {
                              const currentAssets = post.assets || ['page_content'];
                              const newAssets = e.target.checked
                                ? [...currentAssets, asset]
                                : currentAssets.filter(a => a !== asset);
                              // Always keep page_content
                              if (!newAssets.includes('page_content')) {
                                newAssets.unshift('page_content');
                              }
                              handlePostChange(index, 'assets', newAssets);
                            }}
                            className="mr-1 h-3 w-3"
                          />
                          {asset === 'page_content' ? 'Page' : asset.charAt(0).toUpperCase() + asset.slice(1)}
                        </label>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        onClick={() => duplicatePost(index)}
                        className="p-1 text-gray-400 hover:text-blue-600"
                        title="Duplicate"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removePost(index)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {posts.length === 0 && (
          <div className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No posts added yet. Click "Add Post" to get started.</p>
          </div>
        )}
      </div>

      {/* Post Types Reference (Sidebar) */}
      {plan.client_id > 0 && postTypes.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Available Post Types for {clients.find(c => c.id === plan.client_id)?.name}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(postTypesByCategory).map(([category, types]) => (
              <div key={category}>
                <p className="text-xs font-medium text-blue-700 mb-1">{getCategoryLabel(category)}</p>
                <ul className="text-xs text-blue-600 space-y-0.5">
                  {types.map(type => (
                    <li key={type.id}>• {type.name}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/dashboard/plans')}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>

        <div className="flex gap-3">
          <button
            onClick={() => savePlan('draft')}
            disabled={saving}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            Save as Draft
          </button>
          <button
            onClick={() => savePlan('active')}
            disabled={saving}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            Create & Activate Plan
          </button>
        </div>
      </div>
    </div>
  );
}